import React, { useState, useEffect } from "react";
import { Settings, RotateCcw, Crown, AlertCircle } from "lucide-react";

const BashGame = () => {
  // 游戏配置
  const [config, setConfig] = useState({
    totalItems: 21,
    itemType: "火柴",
    itemEmoji: "🔥",
    minTake: 1,
    maxTake: 3,
    allowedTakes: [], // 如果不为空，则只能拿指定数量
    gameMode: "player-vs-player", // 'player-vs-player' 或 'player-vs-ai'
  });

  // 游戏状态
  const [gameState, setGameState] = useState({
    items: Array(21).fill(true), // true表示存在，false表示已被拿走
    currentPlayer: 1, // 1 或 2
    selectedItems: [],
    gameOver: false,
    winner: null,
    message: "",
    messageType: "info", // 'info', 'success', 'error', 'warning'
  });

  const [showSettings, setShowSettings] = useState(false);

  // 物品类型选项
  const itemTypes = [
    { name: "火柴", emoji: "🔥" },
    { name: "硬币", emoji: "🪙" },
    { name: "棒棒糖", emoji: "🍭" },
    { name: "钻石", emoji: "💎" },
    { name: "星星", emoji: "⭐" },
    { name: "花朵", emoji: "🌸" },
  ];

  // 重置游戏
  const resetGame = () => {
    setGameState({
      items: Array(config.totalItems).fill(true),
      currentPlayer: 1,
      selectedItems: [],
      gameOver: false,
      winner: null,
      message: `游戏开始！玩家${
        config.gameMode === "player-vs-ai" ? "1" : "1"
      }先手`,
      messageType: "info",
    });
  };

  // 当配置改变时重置游戏
  useEffect(() => {
    resetGame();
  }, [config]);

  // 获取最大允许选择数量
  const getMaxSelectableCount = () => {
    if (config.allowedTakes.length > 0) {
      return Math.max(...config.allowedTakes);
    }
    return config.maxTake;
  };

  // 选择物品
  const selectItem = (index) => {
    if (gameState.gameOver) return;

    const item = gameState.items[index];
    if (!item) return; // 已被拿走的物品不能选择

    const newSelected = [...gameState.selectedItems];
    const selectedIndex = newSelected.indexOf(index);

    if (selectedIndex > -1) {
      // 取消选择
      newSelected.splice(selectedIndex, 1);
    } else {
      // 检查是否已达到选择上限
      const maxSelectable = getMaxSelectableCount();
      if (newSelected.length >= maxSelectable) {
        setGameState((prev) => ({
          ...prev,
          message: `最多只能选择 ${maxSelectable} 个${config.itemType}！`,
          messageType: "warning",
        }));
        return;
      }
      // 添加选择
      newSelected.push(index);
    }

    setGameState((prev) => ({
      ...prev,
      selectedItems: newSelected,
      message:
        newSelected.length > 0
          ? `已选择 ${newSelected.length} 个${config.itemType}`
          : "",
      messageType: "info",
    }));
  };

  // 验证选择是否有效
  const isValidSelection = (selectedCount) => {
    if (selectedCount === 0) return false;

    if (config.allowedTakes.length > 0) {
      return config.allowedTakes.includes(selectedCount);
    }

    return selectedCount >= config.minTake && selectedCount <= config.maxTake;
  };

  // 确认拿走选中的物品
  const takeSelectedItems = () => {
    const selectedCount = gameState.selectedItems.length;

    if (!isValidSelection(selectedCount)) {
      let errorMsg = "";
      if (config.allowedTakes.length > 0) {
        errorMsg = `只能拿 ${config.allowedTakes.join("、")} 个${
          config.itemType
        }！`;
      } else {
        errorMsg = `每次只能拿 ${config.minTake}-${config.maxTake} 个${config.itemType}！`;
      }

      setGameState((prev) => ({
        ...prev,
        message: errorMsg,
        messageType: "error",
      }));
      return;
    }

    // 移除选中的物品
    const newItems = [...gameState.items];
    gameState.selectedItems.forEach((index) => {
      newItems[index] = false;
    });

    const remainingCount = newItems.filter((item) => item).length;

    // 检查游戏是否结束
    if (remainingCount === 0) {
      setGameState((prev) => ({
        ...prev,
        items: newItems,
        selectedItems: [],
        gameOver: true,
        winner: prev.currentPlayer,
        message: `🎉 玩家${prev.currentPlayer}获胜！`,
        messageType: "success",
      }));
    } else {
      // 切换玩家
      const nextPlayer = gameState.currentPlayer === 1 ? 2 : 1;
      setGameState((prev) => ({
        ...prev,
        items: newItems,
        selectedItems: [],
        currentPlayer: nextPlayer,
        message:
          config.gameMode === "player-vs-ai" && nextPlayer === 2
            ? "AI正在思考..."
            : `轮到玩家${nextPlayer}`,
        messageType: "info",
      }));

      // AI回合
      if (config.gameMode === "player-vs-ai" && nextPlayer === 2) {
        setTimeout(() => {
          aiMove(newItems);
        }, 1000);
      }
    }
  };

  // AI移动（简单策略：尽量让剩余数量为4的倍数）
  const aiMove = (currentItems) => {
    const remainingCount = currentItems.filter((item) => item).length;
    let takeCount;

    if (config.allowedTakes.length > 0) {
      // 如果有指定数量限制，选择最优策略
      const possibleTakes = config.allowedTakes.filter(
        (count) => count <= remainingCount
      );
      takeCount =
        possibleTakes.find(
          (count) => (remainingCount - count) % (config.maxTake + 1) === 0
        ) || possibleTakes[0];
    } else {
      // 标准策略：让剩余数量为(maxTake+1)的倍数
      const optimal = remainingCount % (config.maxTake + 1);
      takeCount =
        optimal === 0 ? Math.min(config.maxTake, remainingCount) : optimal;
      takeCount = Math.max(config.minTake, Math.min(config.maxTake, takeCount));
    }

    // 执行AI移动
    const newItems = [...currentItems];
    let taken = 0;
    for (let i = newItems.length - 1; i >= 0 && taken < takeCount; i--) {
      if (newItems[i]) {
        newItems[i] = false;
        taken++;
      }
    }

    const newRemainingCount = newItems.filter((item) => item).length;

    if (newRemainingCount === 0) {
      setGameState((prev) => ({
        ...prev,
        items: newItems,
        gameOver: true,
        winner: 2,
        message: "🤖 AI获胜！",
        messageType: "success",
      }));
    } else {
      setGameState((prev) => ({
        ...prev,
        items: newItems,
        currentPlayer: 1,
        message: `AI拿走了${taken}个${config.itemType}，轮到玩家1`,
        messageType: "info",
      }));
    }
  };

  // 获取有效拿取范围的描述
  const getTakeRuleDescription = () => {
    if (config.allowedTakes.length > 0) {
      return `只能拿 ${config.allowedTakes.join("、")} 个`;
    }
    return `每次拿 ${config.minTake}-${config.maxTake} 个`;
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-br from-blue-50 to-purple-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-lg p-6">
        {/* 标题栏 */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Crown className="text-yellow-500" />
            Bash游戏
          </h1>
          <div className="flex gap-2">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              onClick={resetGame}
              className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 设置面板 */}
        {showSettings && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-4">
            <h3 className="font-semibold text-lg">游戏设置</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  物品数量
                </label>
                <input
                  type="number"
                  min="3"
                  max="50"
                  value={config.totalItems}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      totalItems: parseInt(e.target.value) || 21,
                    }))
                  }
                  className="w-full p-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  物品类型
                </label>
                <select
                  value={config.itemType}
                  onChange={(e) => {
                    const selected = itemTypes.find(
                      (item) => item.name === e.target.value
                    );
                    setConfig((prev) => ({
                      ...prev,
                      itemType: selected.name,
                      itemEmoji: selected.emoji,
                    }));
                  }}
                  className="w-full p-2 border rounded"
                >
                  {itemTypes.map((item) => (
                    <option key={item.name} value={item.name}>
                      {item.emoji} {item.name}
                    </option>
                  ))}
                </select>
              </div>

              {config.allowedTakes.length === 0 && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      最少拿取
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={config.minTake}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          minTake: parseInt(e.target.value) || 1,
                        }))
                      }
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      最多拿取
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={config.maxTake}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          maxTake: parseInt(e.target.value) || 3,
                        }))
                      }
                      className="w-full p-2 border rounded"
                    />
                  </div>
                </>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  拿取数量限制
                </label>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      id="range-mode"
                      name="take-mode"
                      checked={config.allowedTakes.length === 0}
                      onChange={() =>
                        setConfig((prev) => ({ ...prev, allowedTakes: [] }))
                      }
                      className="w-4 h-4"
                    />
                    <label htmlFor="range-mode" className="text-sm">
                      使用范围限制（最少-最多）
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      id="specific-mode"
                      name="take-mode"
                      checked={config.allowedTakes.length > 0}
                      onChange={() =>
                        setConfig((prev) => ({
                          ...prev,
                          allowedTakes: [1, 2, 3],
                        }))
                      }
                      className="w-4 h-4"
                    />
                    <label htmlFor="specific-mode" className="text-sm">
                      指定特定数量
                    </label>
                  </div>

                  {config.allowedTakes.length > 0 && (
                    <div className="ml-6 p-3 bg-gray-100 rounded">
                      <p className="text-sm mb-2">可选择的数量：</p>
                      <div className="flex flex-wrap gap-2">
                        {Array.from(
                          { length: Math.min(10, config.totalItems) },
                          (_, i) => i + 1
                        ).map((num) => (
                          <label
                            key={num}
                            className="flex items-center gap-1 bg-white px-2 py-1 rounded border cursor-pointer hover:bg-blue-50"
                          >
                            <input
                              type="checkbox"
                              checked={config.allowedTakes.includes(num)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setConfig((prev) => ({
                                    ...prev,
                                    allowedTakes: [
                                      ...prev.allowedTakes,
                                      num,
                                    ].sort((a, b) => a - b),
                                  }));
                                } else {
                                  setConfig((prev) => ({
                                    ...prev,
                                    allowedTakes: prev.allowedTakes.filter(
                                      (n) => n !== num
                                    ),
                                  }));
                                }
                              }}
                              className="w-3 h-3"
                            />
                            <span className="text-sm">{num}</span>
                          </label>
                        ))}
                      </div>
                      {config.allowedTakes.length > 0 ? (
                        <p className="text-xs text-blue-600 mt-2">
                          已选择：{config.allowedTakes.join("、")} 个
                        </p>
                      ) : (
                        <p className="text-xs text-red-500 mt-2">
                          ⚠️ 请至少选择一个数量选项
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  游戏模式
                </label>
                <select
                  value={config.gameMode}
                  onChange={(e) =>
                    setConfig((prev) => ({ ...prev, gameMode: e.target.value }))
                  }
                  className="w-full p-2 border rounded"
                >
                  <option value="player-vs-player">玩家对战</option>
                  <option value="player-vs-ai">人机对战</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* 游戏状态 */}
        <div className="text-center mb-6">
          <div className="bg-gray-100 rounded-lg p-4 mb-4">
            <p className="text-lg font-semibold">
              剩余{config.itemType}:{" "}
              {gameState.items.filter((item) => item).length}
            </p>
            <p className="text-sm text-gray-600">{getTakeRuleDescription()}</p>
          </div>

          {/* 消息提示 */}
          {gameState.message && (
            <div
              className={`flex items-center justify-center gap-2 p-3 rounded-lg mb-4 ${
                gameState.messageType === "error"
                  ? "bg-red-100 text-red-700"
                  : gameState.messageType === "success"
                  ? "bg-green-100 text-green-700"
                  : gameState.messageType === "warning"
                  ? "bg-yellow-100 text-yellow-700"
                  : "bg-blue-100 text-blue-700"
              }`}
            >
              {gameState.messageType === "error" && (
                <AlertCircle className="w-5 h-5" />
              )}
              {gameState.messageType === "success" && (
                <Crown className="w-5 h-5" />
              )}
              <span>{gameState.message}</span>
            </div>
          )}
        </div>

        {/* 游戏区域 */}
        <div className="bg-gray-100 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-7 md:grid-cols-10 gap-2 max-w-3xl mx-auto">
            {gameState.items.map((item, index) => {
              const isSelected = gameState.selectedItems.includes(index);
              const maxSelectable = getMaxSelectableCount();
              const reachedLimit =
                gameState.selectedItems.length >= maxSelectable && !isSelected;

              return (
                <button
                  key={index}
                  onClick={() => selectItem(index)}
                  disabled={!item || gameState.gameOver || reachedLimit}
                  className={`
                    h-16 w-full rounded-lg text-2xl transition-all duration-200 transform
                    ${
                      !item
                        ? "bg-gray-200 opacity-30 cursor-not-allowed"
                        : isSelected
                        ? "bg-yellow-300 scale-110 shadow-lg border-2 border-yellow-500"
                        : reachedLimit
                        ? "bg-gray-300 opacity-50 cursor-not-allowed"
                        : "bg-white hover:bg-yellow-100 hover:scale-105 shadow-md border border-gray-300 cursor-pointer"
                    }
                  `}
                >
                  {item ? config.itemEmoji : "💨"}
                </button>
              );
            })}
          </div>
        </div>

        {/* 操作按钮 */}
        {!gameState.gameOver && gameState.selectedItems.length > 0 && (
          <div className="text-center">
            <button
              onClick={takeSelectedItems}
              disabled={
                gameState.currentPlayer === 2 &&
                config.gameMode === "player-vs-ai"
              }
              className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              拿走选中的{gameState.selectedItems.length}个{config.itemType}
            </button>
          </div>
        )}

        {/* 游戏结束 */}
        {gameState.gameOver && (
          <div className="text-center">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white p-6 rounded-lg shadow-lg">
              <div className="text-4xl mb-2">
                {gameState.winner === 2 && config.gameMode === "player-vs-ai"
                  ? "🤖"
                  : "🎉"}
              </div>
              <h2 className="text-2xl font-bold mb-2">
                {gameState.winner === 2 && config.gameMode === "player-vs-ai"
                  ? "AI获胜！"
                  : `玩家${gameState.winner}获胜！`}
              </h2>
              <button
                onClick={resetGame}
                className="bg-white text-orange-500 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                再来一局
              </button>
            </div>
          </div>
        )}
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default BashGame;
